// Manager-only fund onboarding wizard.
//   Step 1 (Basics, slice 14.a.4) — POST /ops/funds creates the fund.
//   Step 2 (Data sources, slice 14.a.4c) — attach the company-owned accounts the
//     fund tracks + connect their venue keys.
// 14.a.4c.1 (this slice) builds the wizard's navigation + resumability: on a 201
// from Step 1 we persist the created fund + step to localStorage and advance to
// Step 2, which lists the fund's currently-attached accounts. A reload of
// /fund/new restores the in-progress fund so a manager resumes mid-onboarding.
// The "add a data source" form (14.a.4c.2) and inline credential connect/test
// (14.a.4c.4) land in later sub-slices; this ships the list + resume scaffold.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Kicker } from '../../components/shell';
import { CredentialRow } from '../../components/CredentialRow';
import { CreateInvestorForm } from '../../components/CreateInvestorForm';
import { fmtBase, fmtUnitPriceOps } from '../../lib/format';
import { nowLocalForDatetimeMax, isFutureWhen } from '../../lib/datetime';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

// Mirror the backend choice sets (Fund.*_CHOICES). Kept in sync with
// api/apps/portfolio/models.py — these are the values OpsFundsView.post accepts.
const HURDLE_KIND_OPTIONS: [string, string][] = [
  ['none', 'None'],
  ['fixed_annual_pct', 'Fixed annual %'],
  // index_btc is a valid choice the backend accepts, but the performance-accrual
  // service does not yet apply it — label it honestly, matching Fund.tsx settings.
  ['index_btc', 'BTC index (not yet wired)'],
];
const CRYSTALLIZATION_OPTIONS: [string, string][] = [
  ['annual', 'Annual'],
  ['quarterly', 'Quarterly'],
  ['monthly', 'Monthly'],
];
const DEALING_BASIS_OPTIONS: [string, string][] = [
  ['prior_close', 'Prior close (price subscriptions/redemptions off the previous NAV)'],
  ['at_instant', 'At instant (strike a fresh NAV at the moment of dealing)'],
];

interface CreatedFund {
  id: string;
  name: string;
  base_currency: string;
  seed_nav_per_unit: string;
  status: string;
}

interface FundAccount {
  id: string;
  venue: string;
  ownership: string;
  nickname: string;
  chain: string;
  fund_id: string | null;
  status: string;
}

// ─── resumability: persist the in-progress fund across reloads ───────────────
// Client-side wizard state only (which fund am I onboarding, which step). The
// fund itself already lives server-side; this just lets a manager leave + return.
const STORE_KEY = 'rev9.fundOnboard';
// Furthest wizard step that has a render branch (Step 5 = Go live — the last step).
const MAX_BUILT_STEP = 5;

interface SelectedInvestor { id: string; name: string; }
// Completion state persisted so the recorded/struck views survive nav + reload —
// otherwise a Back or refresh re-exposes the subscription form / Strike button and
// invites a duplicate subscription (double unit-issue) or a redundant NAV strike.
interface RecordedSub { units_issued: string; amount_base: string; nav_per_unit_base: string; }
interface StruckNav { struck_at: string; nav_per_unit_base: string; total_nav_base: string; units_outstanding: string; }
interface OnboardState {
  fund: CreatedFund; step: number; investor?: SelectedInvestor;
  subscription?: RecordedSub; navStruck?: StruckNav;
  pendingSubAmount?: string;
}

function readStore(): OnboardState | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as OnboardState;
    const f = v?.fund;
    // Validate the FULL shape we later render (Step 2 dereferences name +
    // base_currency + seed_nav_per_unit). A partial/old-schema value with only
    // an id would otherwise render "$NaN" — reject it and start fresh instead.
    if (f && typeof f.id === 'string' && f.id
        && typeof f.name === 'string'
        && typeof f.base_currency === 'string' && f.base_currency
        && typeof f.seed_nav_per_unit === 'string' && f.seed_nav_per_unit) {
      return v;
    }
  } catch { /* private mode / corrupt value — just start fresh */ }
  return null;
}
function writeStore(v: OnboardState) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}
function clearStore() {
  try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}

const VENUE_LABELS: Record<string, string> = {
  coinbase_exchange: 'Coinbase Exchange',
  // Display-only: bybit is no longer creatable (TODO 19), but any grandfathered
  // row should still render a name rather than the raw slug.
  bybit: 'Bybit',
  kraken: 'Kraken',
  onchain: 'On-chain wallet',
};
const venueLabel = (v: string) => VENUE_LABELS[v] ?? v;

// 0x + 40 hex — mirrors the server's EVM_ADDRESS_RE so a bad address is caught
// client-side before the round-trip.
const EVM_RE = /^0x[0-9a-fA-F]{40}$/;
// demo-dev, operator-review (2026-07): Sui = 0x + 64 hex (66 chars total);
// Solana = base58, no 0x prefix, 32-44 chars typically. Neither matches
// EVM_RE — a fixed single regex was rejecting these chains' real addresses.
const SUI_RE = /^0x[0-9a-fA-F]{64}$/;
const SOLANA_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
// Which regex applies depends on the selected chain — Ethereum/BSC/blank
// (multi-chain EVM) all use the EVM format; Solana/Sui are single-chain-only
// (no "all supported chains" option for them — see CHAIN_OPTIONS below).
const addressRegexFor = (chain: string) =>
  chain === 'solana' ? SOLANA_RE : chain === 'sui' ? SUI_RE : EVM_RE;
const addressHintFor = (chain: string) =>
  chain === 'solana' ? 'Base58 Solana address (no 0x prefix).'
  : chain === 'sui' ? '0x followed by 64 hex characters.'
  : 'EVM addresses starting with 0x followed by 40 hex characters.';
// Only venues the snapshot pipeline can actually serve — mirrors
// SNAPSHOT_SUPPORTED_VENUES in api/apps/portfolio/services/snapshot.py. Bybit was
// offered here until TODO 19 with no integration behind it, so a manager could
// create an account that silently never synced. The API now rejects it too.
const VENUE_OPTIONS: [string, string][] = [
  ['onchain', 'On-chain wallet'],
  ['coinbase_exchange', 'Coinbase Exchange'],
  ['kraken', 'Kraken'],
];
const CHAIN_OPTIONS: [string, string][] = [
  ['', 'All supported chains (Ethereum + BSC)'],
  ['ethereum', 'Ethereum only'],
  ['bsc', 'BNB Smart Chain only'],
  ['solana', 'Solana Chain Only'],
  ['sui', 'Sui Chain Only'],
];

// Map the attach endpoint's structured error_kind → an editorial inline message.
// Exported + reused on the live fund page (Fund.tsx, slice 15.1.b).
export function dataSourceErrorMessage(err: any): string {
  const kind = err?.payload?.error_kind;
  if (kind === 'fund_past_seed') {
    return 'This fund is live (has a struck NAV or an applied subscription). Add a position the fund already holds as an internal data source; new external capital must come in through a subscription.';
  }
  if (kind === 'already_linked') return 'That account is already linked to another fund.';
  if (kind === 'fund_closed') return 'This fund is closed — data sources can no longer be attached.';
  if (kind === 'not_company_owned') return 'That account is not company-owned and cannot back a fund.';
  if (kind === 'account_not_found') return 'That account no longer exists. Refresh and try again.';
  // A DRF FIELD error is `{field: ["msg", ...]}` with no `detail` key, so
  // falling straight through to `detail` swallowed it — the venue_account_id
  // validator added in TODO 21.g produced the vaguest message on this form
  // (c314 ship gate). Surface the first field message instead.
  const payload = err?.payload;
  if (payload && typeof payload === 'object' && !payload.detail) {
    for (const value of Object.values(payload)) {
      const msg = Array.isArray(value) ? value[0] : value;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return payload?.detail || 'Could not add the data source. Check the fields and try again.';
}

export default function FundOnboard() {
  const navigate = useNavigate();

  // Restore an in-progress onboarding once on mount (lazy initializer). Honor the
  // persisted step (not just "2"), clamped to MAX_BUILT_STEP — the furthest step
  // with a render branch — so a stale/future persisted step can't desync the
  // rendered UI from the Stepper highlight. Raise the cap as steps 4/5 are built.
  const [restored] = useState<OnboardState | null>(() => readStore());
  const [step, setStep] = useState<number>(Math.min(restored?.step ?? 1, MAX_BUILT_STEP));
  const [created, setCreated] = useState<CreatedFund | null>(restored?.fund ?? null);
  const [investor, setInvestor] = useState<SelectedInvestor | null>(restored?.investor ?? null);
  const [subscription, setSubscription] = useState<RecordedSub | null>(restored?.subscription ?? null);
  const [navStruck, setNavStruck] = useState<StruckNav | null>(restored?.navStruck ?? null);
  const [pendingSubAmount, setPendingSubAmount] = useState<string | null>(restored?.pendingSubAmount ?? null);

  // Single persistence chokepoint: write the full OnboardState so a nav/reload
  // never drops the chosen investor or the completion state (recorded subscription
  // / struck NAV). Pass overrides for whatever changed; the rest reads current state.
  const persist = (next: {
    step?: number; investor?: SelectedInvestor | null;
    subscription?: RecordedSub | null; navStruck?: StruckNav | null;
    pendingSubAmount?: string | null;
  } = {}) => {
    if (!created) return;
    writeStore({
      fund: created,
      step: next.step ?? step,
      investor: (next.investor !== undefined ? next.investor : investor) ?? undefined,
      subscription: (next.subscription !== undefined ? next.subscription : subscription) ?? undefined,
      navStruck: (next.navStruck !== undefined ? next.navStruck : navStruck) ?? undefined,
      pendingSubAmount: (next.pendingSubAmount !== undefined ? next.pendingSubAmount : pendingSubAmount) ?? undefined,
    });
  };

  // Step 1 — fund basics. Defaults mirror the model column defaults so a
  // manager can create a plain USD fund without typing anything.
  const { user } = useAuth();
  const [name, setName] = useState('');
  // OB.1 — default the fund name to the firm name captured at sign-up, an editable
  // default like every other step-1 field, so "Create fund" never trips native
  // validation on a field that *looked* pre-filled. Fires once when /auth/me loads;
  // clearing the field afterward sticks (the dep doesn't change again).
  useEffect(() => {
    if (user?.client_legal_name) setName(prev => prev || user.client_legal_name);
  }, [user?.client_legal_name]);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [seedNav, setSeedNav] = useState('1');
  const [mgmtFeePct, setMgmtFeePct] = useState('0');
  const [perfFeePct, setPerfFeePct] = useState('0');
  const [hurdleKind, setHurdleKind] = useState('none');
  const [hurdleAnnualPct, setHurdleAnnualPct] = useState('0');
  const [crystallization, setCrystallization] = useState('annual');
  const [dealingBasis, setDealingBasis] = useState('prior_close');
  const [dealingTz, setDealingTz] = useState('Europe/Paris');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // OB.5 — base currency + seed NAV are fixed once the fund is created. Gate
  // "Create fund" behind a one-line soft-confirm echoing those two irreversible
  // values, so a mis-typed base currency isn't only recoverable by recreating.
  const [confirming, setConfirming] = useState(false);

  const startOver = () => {
    clearStore();
    setCreated(null);
    setInvestor(null);
    setSubscription(null);
    setNavStruck(null);
    setStep(1);
    setError(null);
    setConfirming(false);
    // Re-default the fund name on a "start over" too (the effect won't re-fire —
    // user.client_legal_name is unchanged), so reset matches the cold-load default.
    setName(user?.client_legal_name || ''); setBaseCurrency('USD'); setSeedNav('1');
    setMgmtFeePct('0'); setPerfFeePct('0'); setHurdleKind('none');
    setHurdleAnnualPct('0'); setCrystallization('annual');
    setDealingBasis('prior_close'); setDealingTz('Europe/Paris');
  };

  // Gate: validate the name, then open the soft-confirm rather than POST. The
  // actual create runs in doCreate once the operator confirms the irreversibles.
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Give the fund a name.');
      return;
    }
    setConfirming(true);
  };

  const doCreate = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const fund = await api<CreatedFund>('/ops/funds', {
        method: 'POST',
        body: {
          name: name.trim(),
          base_currency: baseCurrency.trim().toUpperCase(),
          seed_nav_per_unit: seedNav.trim() || '1',
          management_fee_annual_pct: mgmtFeePct.trim() || '0',
          performance_fee_pct: perfFeePct.trim() || '0',
          hurdle_kind: hurdleKind,
          // Only meaningful for a fixed hurdle; send 0 otherwise.
          hurdle_annual_pct: hurdleKind === 'fixed_annual_pct' ? (hurdleAnnualPct.trim() || '0') : '0',
          crystallization_cadence: crystallization,
          dealing_basis: dealingBasis,
          dealing_tz: dealingTz.trim() || 'Europe/Paris',
        },
      });
      writeStore({ fund, step: 2 });
      setCreated(fund);
      setStep(2);
    } catch (err: any) {
      const detail = err?.payload?.detail
        ?? (typeof err?.payload === 'object' ? JSON.stringify(err.payload) : null);
      setError(detail || 'Could not create the fund. Check the fields and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (created && step >= 5) {
    return (
      <GoLiveStep
        fund={created}
        onGoToStep={(n) => { setStep(n); persist({ step: n }); }}
        onFinish={() => { clearStore(); navigate('/fund'); }}
        onBack={() => { setStep(4); persist({ step: 4 }); }}
        onStartOver={startOver}
      />
    );
  }
  if (created && step === 4) {
    return (
      <NavStrikeStep
        fund={created}
        struck={navStruck}
        investor={investor}
        pendingSubAmount={pendingSubAmount}
        onStruck={(v) => { setNavStruck(v); persist({ navStruck: v }); }}
        onSubRecorded={(sub) => {
          setSubscription(sub);
          setPendingSubAmount(null);
          persist({ subscription: sub, pendingSubAmount: null });
        }}
        onContinue={() => { setStep(5); persist({ step: 5 }); }}
        onBack={() => { setStep(3); persist({ step: 3 }); }}
        onStartOver={startOver}
      />
    );
  }
  if (created && step === 3) {
    return (
      <InvestorsStep
        fund={created}
        investor={investor}
        subscription={subscription}
        pendingSubAmount={pendingSubAmount}
        onSelectInvestor={(inv) => { setInvestor(inv); persist({ step: 3, investor: inv }); }}
        onRecorded={(sub) => { setSubscription(sub); persist({ subscription: sub }); }}
        onDefer={(amt) => { setPendingSubAmount(amt); persist({ pendingSubAmount: amt }); }}
        onContinue={() => { setStep(4); persist({ step: 4 }); }}
        onBack={() => { setStep(2); persist({ step: 2 }); }}
        onStartOver={startOver}
      />
    );
  }
  if (created && step === 2) {
    return (
      <DataSourcesStep
        fund={created}
        onNext={() => { setStep(3); persist({ step: 3 }); }}
        onStartOver={startOver}
      />
    );
  }

  return (
    <div>
      <Stepper current={1} />
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>01 · Stand up a fund</Kicker>
        <h1 className="page-title">Create a <em>fund.</em></h1>
        <p className="serif-body" style={{ maxWidth: '60ch', color: 'var(--ink-2)', marginTop: 12 }}>
          Name the fund and set its base currency, fees, and dealing rules. Base
          currency and seed NAV per unit are fixed once the fund is created — the
          rest can be changed later from the fund's settings.
        </p>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>

        <Section number="A" label="Identity">
          <Field label="FUND NAME" hint="Shown across the fund surfaces and on every statement.">
            <input className="field-input" required autoFocus
                   value={name} onChange={e => setName(e.target.value)}
                   placeholder="e.g. Halcyon Digital Fund" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <Field label="BASE CURRENCY" hint="ISO code (USD, EUR, GBP). Fixed after creation.">
              <input className="field-input"
                     value={baseCurrency}
                     onChange={e => setBaseCurrency(e.target.value.toUpperCase())}
                     placeholder="USD"
                     style={{ fontFamily: 'var(--mono)', textTransform: 'uppercase' }} />
            </Field>
            <Field label="SEED NAV PER UNIT" hint="Starting price per unit; the first subscription mints units at this price. Fixed after creation.">
              <input className="field-input num" type="number" step="0.01" min="0.01"
                     value={seedNav} onChange={e => setSeedNav(e.target.value)}
                     placeholder="1" />
            </Field>
          </div>
        </Section>

        <Section number="B" label="Fees">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <Field label="MANAGEMENT FEE (% / YEAR)" hint="Accrued daily on net assets. Leave 0 if none.">
              <input className="field-input num" type="number" step="0.01" min="0"
                     value={mgmtFeePct} onChange={e => setMgmtFeePct(e.target.value)}
                     placeholder="0" />
            </Field>
            <Field label="PERFORMANCE FEE (%)" hint="Share of gains above the hurdle, crystallized on the cadence below. Leave 0 if none.">
              <input className="field-input num" type="number" step="0.01" min="0"
                     value={perfFeePct} onChange={e => setPerfFeePct(e.target.value)}
                     placeholder="0" />
            </Field>
          </div>
          <Field label="HURDLE" hint="The return a fund must clear before a performance fee applies.">
            <select className="field-input" value={hurdleKind}
                    onChange={e => setHurdleKind(e.target.value)}>
              {HURDLE_KIND_OPTIONS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </Field>
          {hurdleKind === 'fixed_annual_pct' && (
            <Field label="HURDLE RATE (% / YEAR)" hint="Annual return the fund must beat before performance fees accrue.">
              <input className="field-input num" type="number" step="0.01" min="0"
                     value={hurdleAnnualPct} onChange={e => setHurdleAnnualPct(e.target.value)}
                     placeholder="0" />
            </Field>
          )}
          <Field label="CRYSTALLIZATION" hint="How often accrued performance fees are locked in.">
            <select className="field-input" value={crystallization}
                    onChange={e => setCrystallization(e.target.value)}>
              {CRYSTALLIZATION_OPTIONS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </Field>
        </Section>

        <Section number="C" label="Dealing">
          <Field label="DEALING BASIS" hint="How subscriptions and redemptions are priced.">
            <select className="field-input" value={dealingBasis}
                    onChange={e => setDealingBasis(e.target.value)}>
              {DEALING_BASIS_OPTIONS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="DEALING TIME ZONE" hint="IANA time zone for the dealing cutoff (e.g. Europe/Paris, America/New_York).">
            <input className="field-input"
                   value={dealingTz} onChange={e => setDealingTz(e.target.value)}
                   placeholder="Europe/Paris"
                   style={{ fontFamily: 'var(--mono)' }} />
          </Field>
        </Section>

        {error && (
          <div style={{
            fontFamily: 'var(--serif)', fontStyle: 'italic',
            color: 'var(--oxblood)', fontSize: 15,
          }}>
            {error}
          </div>
        )}

        <div style={{ paddingTop: 16, borderTop: '1px solid var(--ink)' }}>
          {confirming ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* OB.5 — echo the two irreversible fields off live state so the */}
              {/* confirmed values always match what gets POSTed.                */}
              <p className="serif-body" style={{ margin: 0, maxWidth: '60ch', color: 'var(--ink-2)' }}>
                Base currency{' '}
                <strong style={{ fontFamily: 'var(--mono)' }}>{(baseCurrency.trim().toUpperCase() || 'USD')}</strong>
                {' '}and seed NAV{' '}
                <strong style={{ fontFamily: 'var(--mono)' }}>{(seedNav.trim() || '1')}</strong>
                {' '}per unit are fixed once the fund is created — they can't be changed later.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <button type="button" className="btn" disabled={submitting} onClick={doCreate}>
                  {submitting ? 'Creating…' : <>Create fund <span className="btn-arrow">→</span></>}
                </button>
                <button type="button" className="btn-link" disabled={submitting}
                        onClick={() => setConfirming(false)}>
                  Review fields
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <button type="submit" className="btn" disabled={submitting}>
                {submitting ? 'Creating…' : <>Create fund <span className="btn-arrow">→</span></>}
              </button>
              <button type="button" className="btn-link" onClick={() => navigate('/fund')}>
                ← Back to fund
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── Step 2 — data sources ───────────────────────────────────────────────────
function DataSourcesStep({ fund, onNext, onStartOver }: {
  fund: CreatedFund; onNext: () => void; onStartOver: () => void;
}) {
  const [accounts, setAccounts] = useState<FundAccount[] | null>(null);
  // Credential metadata per coinbase_exchange account — the fund-accounts GET
  // omits has_credentials/key_last_four, so fetch GET /ops/accounts/{id}/credentials
  // per CEX account to feed the shared CredentialRow (14.a.4c.4).
  const [credMeta, setCredMeta] = useState<Record<string, { has_credentials: boolean; key_last_four: string | null; credential_status: string | null }>>({});
  // CEX accounts whose credential-metadata fetch failed this round — rendered as
  // "status unavailable" rather than wrongly asserting NOT CONNECTED (which would
  // invite re-connecting over a working key). credential_status (incl. needs_reauth)
  // IS surfaced (14.a.6) — the per-account credentials GET now returns it — so the
  // CredentialRow shows a NEEDS REAUTH warning for an expired key.
  const [credErr, setCredErr] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // List the fund's attached accounts + each CEX account's credential metadata;
  // re-fetch when a data source is added or a key is connected/revoked (reloadKey).
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    api<FundAccount[]>(`/ops/funds/${fund.id}/accounts`)
      .then(async rows => {
        if (cancelled) return;
        setAccounts(rows);
        const cex = rows.filter(a => a.venue === 'coinbase_exchange');
        const metas = await Promise.all(cex.map(a =>
          api<{ has_credentials: boolean; key_last_four: string | null; credential_status: string | null }>(`/ops/accounts/${a.id}/credentials`)
            .then(m => [a.id, { has_credentials: m.has_credentials, key_last_four: m.key_last_four, credential_status: m.credential_status ?? null }] as const)
            .catch(() => [a.id, null] as const)));
        if (cancelled) return;
        const ok: Record<string, { has_credentials: boolean; key_last_four: string | null; credential_status: string | null }> = {};
        const failed = new Set<string>();
        for (const [id, m] of metas) { if (m) ok[id] = m; else failed.add(id); }
        // Merge (don't reset) so a successful prior value survives a transient
        // re-fetch failure and rows don't flash to NOT CONNECTED.
        setCredMeta(prev => ({ ...prev, ...ok }));
        setCredErr(failed);
      })
      .catch(() => { if (!cancelled) setErr('Could not load this fund’s data sources.'); });
    return () => { cancelled = true; };
  }, [fund.id, reloadKey]);

  return (
    <div>
      <Stepper current={2} />
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>02 · Data sources</Kicker>
        <h1 className="page-title">Data <em>sources.</em></h1>
        <p className="serif-body" style={{ maxWidth: '60ch', color: 'var(--ink-2)', marginTop: 12 }}>
          {fund.name} is created. Attach the accounts it tracks — exchange accounts
          and on-chain wallets — so it can value holdings. Until a data source is
          connected, the fund shows no holdings.
        </p>
      </div>

      <div style={{ borderTop: '1px solid var(--ink)', paddingTop: 24, marginBottom: 40,
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
        <Summary label="FUND" value={fund.name} />
        <Summary label="BASE CURRENCY" value={fund.base_currency} mono />
        <Summary label="SEED NAV / UNIT"
                 value={fmtUnitPriceOps(Number(fund.seed_nav_per_unit), fund.base_currency)} mono />
      </div>

      <AddDataSourceForm fundId={fund.id} onAdded={() => setReloadKey(k => k + 1)} funding="internal" />

      <section>
        <div className="label-s" style={{ color: 'var(--ink-3)', marginBottom: 12 }}>
          ATTACHED DATA SOURCES
        </div>
        {err ? (
          <p className="serif-body" style={{ color: 'var(--oxblood)', fontStyle: 'italic' }}>{err}</p>
        ) : accounts === null ? (
          <p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p>
        ) : accounts.length === 0 ? (
          <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--ink-2)', maxWidth: '60ch' }}>
            No data sources attached yet. The next step connects the exchange
            accounts and on-chain wallets this fund tracks.
          </p>
        ) : (
          <div>
            {accounts.map(a => {
              // Coinbase Exchange accounts get the full inline credential control
              // (the shared CredentialRow does connect/test/rotate/revoke). The
              // fund-accounts GET lacks credential metadata, so map in what we
              // fetched per-account and use nickname as the row label.
              const m = credMeta[a.id];
              if (a.venue === 'coinbase_exchange' && m) {
                return (
                  <CredentialRow
                    key={a.id}
                    account={{
                      id: a.id,
                      label: a.nickname || venueLabel(a.venue),
                      venue: a.venue,
                      has_credentials: m.has_credentials,
                      key_last_four: m.key_last_four,
                      credential_status: m.credential_status,
                    }}
                    onChange={() => setReloadKey(k => k + 1)}
                  />
                );
              }
              // Plain row for: on-chain (no key needed), a grandfathered Bybit
              // row (test probe is Coinbase-only), or a CEX account still loading / whose credential
              // fetch failed (show loading or "unavailable", never a misleading
              // NOT CONNECTED that would invite re-connecting over a working key).
              let note: string;
              if (a.venue === 'onchain') note = 'No API key required — balances are read on-chain.';
              else if (a.venue === 'coinbase_exchange') {
                note = credErr.has(a.id)
                  ? 'Connection status unavailable — reload to retry.'
                  : 'Checking connection…';
              } else note = 'Key connect/test for this venue is not available here yet.';
              return (
                <div key={a.id} style={{
                  borderBottom: 'var(--hairline)', padding: '18px 0',
                  display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 28, alignItems: 'center',
                }}>
                  <span className="label-s">{(a.nickname || venueLabel(a.venue)).toUpperCase()}</span>
                  <div>
                    <span className="label-s" style={{
                      color: (a.venue === 'coinbase_exchange' && credErr.has(a.id)) ? 'var(--oxblood)' : 'var(--ink-2)',
                    }}>
                      {venueLabel(a.venue)}{a.chain ? ` · ${a.chain.toUpperCase()}` : ''} · {a.status.toUpperCase()}
                    </span>
                    <span className="serif-body" style={{ display: 'block', fontSize: 13, marginTop: 6, color: 'var(--ink-3)' }}>
                      {note}
                    </span>
                  </div>
                  <span />
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingTop: 24, marginTop: 24,
                    borderTop: '1px solid var(--ink)' }}>
        <button type="button" className="btn" disabled={!accounts || accounts.length === 0} onClick={onNext}>
          Add investors <span className="btn-arrow">→</span>
        </button>
        <Link to="/fund" className="btn-link" style={{ textDecoration: 'none' }}>
          View your fund
        </Link>
        <button type="button" className="btn-link" onClick={onStartOver}>
          Start a new fund
        </button>
      </div>
      {accounts && accounts.length === 0 && (
        <p className="label-s" style={{ color: 'var(--ink-3)', marginTop: 12 }}>
          Attach at least one data source above before adding investors — a fund
          with no data source can't value what investors hold.
        </p>
      )}
    </div>
  );
}

interface ClientRow { id: string; display_name: string; legal_name: string; }

// ─── Step 3 — investors. 5b: pick an existing tenant client as the fund's first
// investor (create-new lands in 5b2; the subscription form in 5c). ──────────────
function InvestorsStep({ fund, investor, subscription, pendingSubAmount, onSelectInvestor, onRecorded, onDefer, onContinue, onBack, onStartOver }: {
  fund: CreatedFund;
  investor: SelectedInvestor | null;
  subscription: RecordedSub | null;
  pendingSubAmount: string | null;
  onSelectInvestor: (inv: SelectedInvestor) => void;
  onRecorded: (sub: RecordedSub) => void;
  onDefer: (amount: string) => void;
  onContinue: () => void;
  onBack: () => void;
  onStartOver: () => void;
}) {
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState(false);  // 5b2b: "create new investor" mode

  useEffect(() => {
    let cancelled = false;
    api<ClientRow[]>('/ops/clients')
      // 16.b.6.f: company accounts are fund-owned (client=None) now; /ops/clients
      // returns only real investors, so no house-client filtering is needed.
      .then(rows => { if (!cancelled) setClients(rows); })
      .catch(() => { if (!cancelled) setErr('Could not load clients.'); });
    return () => { cancelled = true; };
  }, []);

  const showPicker = picking || !investor;

  return (
    <div>
      <Stepper current={3} />
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>03 · Investors</Kicker>
        <h1 className="page-title">Add an <em>investor.</em></h1>
        <p className="serif-body" style={{ maxWidth: '60ch', color: 'var(--ink-2)', marginTop: 12 }}>
          Choose the fund's first investor from your existing clients. Recording
          their initial subscription — priced off the fund's NAV — is the next step.
        </p>
      </div>

      <section style={{ marginBottom: 40 }}>
        <div className="label-s" style={{ color: 'var(--ink-3)', marginBottom: 12 }}>INVESTOR</div>

        {!showPicker && investor ? (
          <>
            <div style={{ borderTop: '1px solid var(--ink)', borderBottom: 'var(--hairline)',
                          padding: '18px 0', display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', gap: 16 }}>
              <span style={{ fontSize: 16 }}>{investor.name}</span>
              <button type="button" className="btn-link" onClick={() => setPicking(true)}>Change</button>
            </div>
            <SubscriptionForm fund={fund} investor={investor} subscription={subscription}
                              pendingSubAmount={pendingSubAmount}
                              onRecorded={onRecorded} onDefer={onDefer} onContinue={onContinue} />
          </>
        ) : err ? (
          <p className="serif-body" style={{ color: 'var(--oxblood)', fontStyle: 'italic' }}>{err}</p>
        ) : clients === null ? (
          <p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p>
        ) : creating ? (
          <CreateInvestorForm
            onCreated={(inv) => { onSelectInvestor(inv); setPicking(false); setCreating(false); }}
            onCancel={() => setCreating(false)}
          />
        ) : (
          <>
            {clients.length === 0 ? (
              <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--ink-2)', maxWidth: '60ch' }}>
                No existing clients to choose from — create the fund's first investor below.
              </p>
            ) : (
              <div>
                {clients.map((c, i) => (
                  <div key={c.id} style={{
                    borderTop: i === 0 ? '1px solid var(--ink)' : 'var(--hairline)',
                    borderBottom: i === clients.length - 1 ? 'var(--hairline)' : undefined,
                    padding: '14px 0', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 16,
                  }}>
                    <div>
                      <div style={{ fontSize: 15 }}>{c.display_name}</div>
                      <div className="label-s" style={{ color: 'var(--ink-3)', marginTop: 4 }}>{c.legal_name}</div>
                    </div>
                    <button type="button" className="btn-link"
                            onClick={() => { onSelectInvestor({ id: c.id, name: c.display_name }); setPicking(false); }}>
                      Select <span className="btn-arrow">→</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="btn-link" style={{ marginTop: 16, alignSelf: 'flex-start' }}
                    onClick={() => setCreating(true)}>
              + Create a new investor
            </button>
          </>
        )}
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingTop: 24,
                    borderTop: '1px solid var(--ink)' }}>
        <button type="button" className="btn-link" onClick={onBack}>
          ← Back to data sources
        </button>
        <button type="button" className="btn-link" onClick={onStartOver}>
          Start a new fund
        </button>
      </div>
    </div>
  );
}

// Step 3 part 2 (14.a.5c): record the investor's first subscription, previewing the
// NAV it will price against before confirming. The preview (GET …/subscriptions/
// preview) is informational — its nav_per_unit is USD-denominated, so the implied
// units shown are an "≈" estimate (exact for USD funds, the wizard default); the
// AUTHORITATIVE units come from the POST response (units_issued, base-derived).
function SubscriptionForm({ fund, investor, subscription, pendingSubAmount, onRecorded, onDefer, onContinue }: {
  fund: CreatedFund; investor: SelectedInvestor;
  subscription: RecordedSub | null;
  pendingSubAmount: string | null;
  onRecorded: (sub: RecordedSub) => void;
  onDefer: (amount: string) => void;
  onContinue: () => void;
}) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [amount, setAmount] = useState(pendingSubAmount ?? '');
  // Operator-review ask (2026-07): the fund's first investor didn't
  // necessarily join "right now" — a manager backfilling real history needs
  // to set the actual join date. Defaults to blank (server defaults to now).
  const [when, setWhen] = useState('');
  const [preview, setPreview] = useState<any | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fmtUnits = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });

  const doPreview = async () => {
    setPreviewErr(null); setPreview(null); setPreviewing(true);
    try {
      const q = when ? `?effective_at=${encodeURIComponent(new Date(when).toISOString())}` : '';
      setPreview(await api<any>(`/ops/funds/${fund.id}/subscriptions/preview${q}`));
    } catch (e: any) {
      if (e?.payload?.error_kind === 'no_valuation_for_dealing_date') {
        setPreviewErr('This fund has no struck NAV yet — strike a NAV first (the next step), then preview the subscription.');
      } else {
        setPreviewErr(e?.payload?.detail || 'Could not preview the NAV.');
      }
    } finally { setPreviewing(false); }
  };

  const confirm = async () => {
    setError(null); setConfirming(true);
    try {
      const sub = await api<any>(`/ops/funds/${fund.id}/subscriptions`, {
        method: 'POST', body: {
          investor_id: investor.id, amount: amount.trim(),
          ...(when ? { effective_at: new Date(when).toISOString() } : {}),
        },
      });
      // Lift the recorded subscription to the parent so it persists across nav +
      // reload — re-mounting this form must NOT re-show the form (a re-confirm
      // would double-issue units; the POST has no duplicate guard).
      onRecorded({
        units_issued: String(sub.units_issued), amount_base: String(sub.amount_base),
        nav_per_unit_base: String(sub.nav_per_unit_base),
      });
    } catch (e: any) {
      const msg = e?.payload?.detail || '';
      if (e?.payload?.error_kind === 'no_valuation_for_dealing_date' ||
          msg.includes('no FundValuation') || msg.includes('dealing window')) {
        // No NAV struck yet — save the amount and hand off to Step 4 (strike NAV).
        onDefer(amount.trim());
        onContinue();
        return;
      }
      setError(msg || 'Could not record the subscription.');
    } finally { setConfirming(false); }
  };

  if (subscription) {
    return (
      <div style={{ marginTop: 24 }}>
        <p className="serif-body" style={{ fontSize: 15, color: 'var(--ink-2)' }}>
          Subscription recorded — <span className="num">{fmtBase(Number(subscription.amount_base), fund.base_currency)}</span> at{' '}
          <span className="num">{fmtUnitPriceOps(Number(subscription.nav_per_unit_base), fund.base_currency)}</span>/unit →{' '}
          <span className="num">{fmtUnits(Number(subscription.units_issued))}</span> units.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 16 }}>
          <button type="button" className="btn" onClick={onContinue}>
            Continue to NAV strike <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  const amt = Number(amount);
  const navpu = preview ? Number(preview.nav_per_unit) : 0;
  // amount is base-currency, navpu is USD — the ratio is only dimensionally
  // meaningful for USD funds. Skip the estimate for non-USD (authoritative
  // units come from the POST response either way).
  const approxUnits = preview && navpu > 0 && amt > 0 && fund.base_currency === 'USD'
    ? amt / navpu : null;

  return (
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
      <Field label={`SUBSCRIPTION AMOUNT (${fund.base_currency})`} hint="The investor's initial capital into the fund.">
        <input className="field-input num" type="number" step="0.01" min="0"
               value={amount} onChange={e => setAmount(e.target.value)}
               placeholder="e.g. 100000" />
      </Field>

      <Field label="SUBSCRIBED ON" hint="When this investor actually joined — leave blank for right now. Backdate this to record real history.">
        <input className="field-input" type="datetime-local" value={when} max={nowLocalForDatetimeMax()}
               onChange={e => setWhen(e.target.value)} />
        {isFutureWhen(when) && (
          <p className="serif-body" style={{ marginTop: 6, fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 13 }}>
            Date/time cannot be in the future.
          </p>
        )}
      </Field>

      <div>
        <button type="button" className="btn-link" onClick={doPreview} disabled={previewing}>
          {previewing ? 'Pricing…' : 'Preview NAV'}
        </button>
      </div>

      {previewErr && (
        <div>
          <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14, marginBottom: 12 }}>{previewErr}</p>
          {previewErr.includes('no struck NAV') && amt > 0 && (
            <button type="button" className="btn" onClick={() => { onDefer(amount.trim()); onContinue(); }}>
              Save amount — go strike NAV <span className="btn-arrow">→</span>
            </button>
          )}
        </div>
      )}
      {preview && (
        <div style={{ borderTop: 'var(--hairline)', borderBottom: 'var(--hairline)', padding: '14px 0' }}>
          <div className="label-s" style={{ color: 'var(--ink-3)' }}>WILL PRICE AT</div>
          <div style={{ fontSize: 15, marginTop: 4 }}>
            {/* The preview's nav_per_unit is USD-denominated — label it USD (a
                "(USD ref.)" tag for non-USD funds); base-currency units are
                confirmed on subscribe (units_issued in the recorded view). */}
            <span className="num">{fmtUnitPriceOps(navpu, 'USD')}</span>/unit{fund.base_currency !== 'USD' ? ' (USD ref.)' : ''}
            <span className="label-s" style={{ color: 'var(--ink-3)', marginLeft: 12 }}>
              NAV STRUCK {preview.will_price_at
                ? new Date(preview.will_price_at).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '—'}
            </span>
          </div>
          {approxUnits != null && (
            <div className="serif-body" style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6 }}>
              ≈ <span className="num">{fmtUnits(approxUnits)}</span> units (exact units confirmed on subscribe)
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14 }}>{error}</p>
      )}

      <div>
        <button type="button" className="btn" disabled={confirming || !(amt > 0) || isFutureWhen(when)} onClick={confirm}>
          {confirming ? 'Recording…' : <>Confirm subscription <span className="btn-arrow">→</span></>}
        </button>
      </div>
    </div>
  );
}

// ─── Step 4 — strike NAV (14.a.5d). POST /ops/funds/{id}/nav-strike records a
// FundValuation; the response carries base-currency figures (nav_per_unit_base,
// total_nav_base) so fmtBase is currency-correct here. Go-live (Step 5) is 5e. ──
function NavStrikeStep({ fund, struck, investor, pendingSubAmount, onStruck, onSubRecorded, onContinue, onBack, onStartOver }: {
  fund: CreatedFund; struck: StruckNav | null;
  investor: SelectedInvestor | null;
  pendingSubAmount: string | null;
  onStruck: (v: StruckNav) => void;
  onSubRecorded: (sub: RecordedSub) => void;
  onContinue: () => void;
  onBack: () => void; onStartOver: () => void;
}) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postingDeferred, setPostingDeferred] = useState(false);

  const fmtU = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });

  const strike = async () => {
    setError(null); setBusy(true);
    try {
      const v = await api<any>(`/ops/funds/${fund.id}/nav-strike`, { method: 'POST', body: {} });
      // Lift to the parent so the struck NAV persists across reload — otherwise a
      // refresh re-exposes the Strike button and a re-strike writes a second
      // near-duplicate FundValuation that wins the prior_close price lookup.
      onStruck({
        struck_at: String(v.struck_at), nav_per_unit_base: String(v.nav_per_unit_base),
        total_nav_base: String(v.total_nav_base), units_outstanding: String(v.units_outstanding),
      });
      // Auto-post the deferred subscription now that we have a valid NAV.
      if (pendingSubAmount && investor) {
        setPostingDeferred(true);
        try {
          const sub = await api<any>(`/ops/funds/${fund.id}/subscriptions`, {
            method: 'POST', body: { investor_id: investor.id, amount: pendingSubAmount },
          });
          onSubRecorded({
            units_issued: String(sub.units_issued), amount_base: String(sub.amount_base),
            nav_per_unit_base: String(sub.nav_per_unit_base),
          });
        } catch (se: any) {
          setError('NAV struck but could not auto-post the subscription: ' + (se?.payload?.detail || 'unknown error'));
        } finally { setPostingDeferred(false); }
      }
    } catch (e: any) {
      setError(e?.payload?.detail || 'Could not strike the NAV.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Stepper current={4} />
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>04 · Strike NAV</Kicker>
        <h1 className="page-title">Strike the <em>NAV.</em></h1>
        <p className="serif-body" style={{ maxWidth: '60ch', color: 'var(--ink-2)', marginTop: 12 }}>
          Record the fund's net asset value from its current holdings. This sets the
          price per unit your investors' positions are valued at.
        </p>
      </div>

      {struck ? (
        <>
          <div style={{ borderTop: '1px solid var(--ink)', paddingTop: 24, marginBottom: 24,
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            <Summary label="NAV PER UNIT"
                     value={fmtUnitPriceOps(Number(struck.nav_per_unit_base), fund.base_currency)} mono />
            <Summary label="NET ASSET VALUE"
                     value={fmtBase(Number(struck.total_nav_base), fund.base_currency)} mono />
            <Summary label="UNITS OUTSTANDING" value={fmtU(Number(struck.units_outstanding))} mono />
          </div>
          {postingDeferred && (
            <p className="label-s" style={{ color: 'var(--ink-3)', marginBottom: 16 }}>Recording subscription…</p>
          )}
          {error && (
            <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14, marginBottom: 16 }}>{error}</p>
          )}
          <div style={{ marginBottom: 24 }}>
            <button type="button" className="btn" onClick={onContinue} disabled={postingDeferred}>
              Continue to go-live <span className="btn-arrow">→</span>
            </button>
          </div>
          <p className="serif-body" style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)', marginBottom: 24 }}>
            NAV struck {struck.struck_at
              ? new Date(struck.struck_at).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : ''}
            {' '}· the go-live readiness check is the next step.
          </p>
        </>
      ) : (
        <section style={{ marginBottom: 40 }}>
          {pendingSubAmount && investor && (
            <p className="serif-body" style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 16, fontStyle: 'italic' }}>
              Subscription of {fmtBase(Number(pendingSubAmount), fund.base_currency)} for {investor.name} is
              queued — it will be recorded automatically after NAV is struck.
            </p>
          )}
          <button type="button" className="btn" disabled={busy} onClick={strike}>
            {busy ? (pendingSubAmount ? 'Striking + recording subscription…' : 'Striking…') : <>Strike NAV <span className="btn-arrow">→</span></>}
          </button>
          {error && (
            <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14, marginTop: 16 }}>
              {error}
            </p>
          )}
        </section>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingTop: 24,
                    borderTop: '1px solid var(--ink)' }}>
        <button type="button" className="btn-link" onClick={onBack}>← Back to investors</button>
        <button type="button" className="btn-link" onClick={onStartOver}>Start a new fund</button>
      </div>
    </div>
  );
}

// ─── Step 5 — go live (14.a.5e). Re-check readiness against live GETs and confirm
// the fund is live (or state what's missing, with a jump back to the right step).
// All GETs already shipped + manager-gated; no new endpoint. ────────────────────
function GoLiveStep({ fund, onGoToStep, onFinish, onBack, onStartOver }: {
  fund: CreatedFund; onGoToStep: (n: number) => void; onFinish: () => void;
  onBack: () => void; onStartOver: () => void;
}) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [accounts, setAccounts] = useState<any[] | null>(null);
  const [credReady, setCredReady] = useState<Record<string, boolean>>({});
  const [credErr, setCredErr] = useState(false);       // a keyed account's cred probe failed
  const [credChecked, setCredChecked] = useState(false);
  const [subCount, setSubCount] = useState<number | null>(null);
  const [navCount, setNavCount] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Read-only readiness probe. NOTE: use GET /subscriptions (a plain list) for
    // the investor gate — NOT GET /investors, which is write-on-read
    // (list_investors → strike_nav writes a FundValuation), which would
    // self-fulfill the NAV gate + pollute the price series on every go-live load.
    Promise.all([
      api<any[]>(`/ops/funds/${fund.id}/accounts`),
      api<any[]>(`/ops/funds/${fund.id}/subscriptions`),
      api<any[]>(`/ops/funds/${fund.id}/nav-strike`),
    ]).then(async ([accts, subs, navs]) => {
      if (cancelled) return;
      setAccounts(accts); setSubCount(subs.length); setNavCount(navs.length);
      // A keyed venue (any CEX) is "ready" once it has stored credentials;
      // on-chain accounts need no key.
      const keyed = accts.filter(a => a.venue !== 'onchain');
      if (keyed.length === 0) { if (!cancelled) setCredChecked(true); return; }
      const creds = await Promise.all(keyed.map(a =>
        api<any>(`/ops/accounts/${a.id}/credentials`)
          .then(m => [a.id, !!m.has_credentials, false] as const)
          .catch(() => [a.id, false, true] as const)));
      if (cancelled) return;
      setCredReady(Object.fromEntries(creds.map(([id, ok]) => [id, ok])));
      setCredErr(creds.some(([, , e]) => e));
      setCredChecked(true);
    }).catch(() => { if (!cancelled) setErr('Could not check go-live readiness.'); });
    return () => { cancelled = true; };
  }, [fund.id]);

  const hasKeyed = !!accounts && accounts.some(a => a.venue !== 'onchain');
  // Hold "loading" until the per-account cred probe resolves too, so a keyed fund
  // never flashes a wrong "NEEDED" before credentials are known.
  const loading = !err && (accounts === null || subCount === null || navCount === null || (hasKeyed && !credChecked));
  const accountReady = !!accounts && accounts.length > 0 &&
    accounts.some(a => a.venue === 'onchain' || credReady[a.id]);

  const gates = [
    { label: 'Fund created', pass: true, hint: '', step: 1 },
    {
      label: 'Data source connected', pass: accountReady, step: 2,
      // Distinguish a genuine "no key" from a failed cred probe (reload to retry)
      // so a transient outage doesn't read as a missing key on the final screen.
      hint: credErr
        ? 'Could not verify a key — reload to retry, or check Data sources.'
        : 'Attach an account and connect its key under Data sources.',
    },
    { label: 'Investor subscribed', pass: (subCount ?? 0) > 0, hint: 'Add an investor and record a subscription under Investors.', step: 3 },
    { label: 'First NAV struck', pass: (navCount ?? 0) > 0, hint: 'Strike the fund’s first NAV under Strike NAV.', step: 4 },
  ];
  const allPass = !loading && !err && gates.every(g => g.pass);

  return (
    <div>
      <Stepper current={5} />
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>05 · Go live</Kicker>
        <h1 className="page-title">{allPass ? <>Your fund is <em>live.</em></> : <>Go <em>live.</em></>}</h1>
        <p className="serif-body" style={{ maxWidth: '60ch', color: 'var(--ink-2)', marginTop: 12 }}>
          {allPass
            ? `${fund.name} is fully set up — tracking holdings, with an investor subscribed and a NAV struck.`
            : 'A fund is ready to go live once it has a connected data source, a subscribed investor, and a struck NAV.'}
        </p>
      </div>

      <section style={{ marginBottom: 40 }}>
        {err ? (
          <p className="serif-body" style={{ color: 'var(--oxblood)', fontStyle: 'italic' }}>{err}</p>
        ) : loading ? (
          <p className="label-s" style={{ color: 'var(--ink-3)' }}>Checking readiness…</p>
        ) : (
          gates.map((g, i) => (
            <div key={g.label} style={{
              borderTop: i === 0 ? '1px solid var(--ink)' : 'var(--hairline)',
              borderBottom: i === gates.length - 1 ? 'var(--hairline)' : undefined,
              padding: '16px 0', display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: 24, alignItems: 'baseline',
            }}>
              <span className="label-s" style={{ color: g.pass ? 'var(--accent)' : 'var(--oxblood)' }}>
                {g.pass ? 'READY' : 'NEEDED'}
              </span>
              <div>
                <div style={{ fontSize: 15 }}>{g.label}</div>
                {!g.pass && g.hint && (
                  <div className="serif-body" style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 4 }}>
                    {g.hint}
                  </div>
                )}
              </div>
              {!g.pass && g.step >= 2 ? (
                <button type="button" className="btn-link" onClick={() => onGoToStep(g.step)}>Go to step</button>
              ) : <span />}
            </div>
          ))
        )}
      </section>

      {allPass && (
        <div style={{ marginBottom: 40 }}>
          <button type="button" className="btn" onClick={onFinish}>
            View your fund <span className="btn-arrow">→</span>
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingTop: 24,
                    borderTop: '1px solid var(--ink)' }}>
        <button type="button" className="btn-link" onClick={onBack}>← Back to NAV strike</button>
        <button type="button" className="btn-link" onClick={onStartOver}>Start a new fund</button>
      </div>
    </div>
  );
}

// Add a data source: create a company-owned account (POST /ops/accounts, 14.a.4b)
// then attach it to the fund (POST /ops/funds/{id}/accounts, 14.a.2). On success
// bumps the parent's reloadKey so the attached list refreshes. Credential
// connect/test is a separate sub-slice (14.a.4c.4).
// Exported for reuse on the live fund page (Fund.tsx, slice 15.1.b). `funding`
// is threaded into the attach call: the wizard (pre-seed) keeps the default
// 'external'; the live fund page passes 'internal' so the attach is allowed on
// a fund that has already struck a NAV (15.1.a).
export function AddDataSourceForm(
  { fundId, onAdded, funding = 'external' }:
  { fundId: string; onAdded: () => void; funding?: 'internal' | 'external' },
) {
  const [venue, setVenue] = useState('');
  const [nickname, setNickname] = useState('');
  const [venueAccountId, setVenueAccountId] = useState('');
  const [chain, setChain] = useState('');
  const [wallets, setWallets] = useState<string[]>(['']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setVenue(''); setNickname(''); setVenueAccountId(''); setChain(''); setWallets(['']);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!venue) { setError('Pick a venue for this data source.'); return; }

    const body: Record<string, unknown> = { venue, nickname: nickname.trim() };
    if (venue === 'onchain') {
      const addrs = wallets.map(s => s.trim()).filter(Boolean);
      if (!addrs.length) {
        setError('Add at least one wallet address. For testing, you can use ' +
          '0x0000000000000000000000000000000000000001 — a validly-formatted ' +
          'address with no real funds behind it.');
        return;
      }
      const addrRe = addressRegexFor(chain);
      const bad = addrs.find(a => !addrRe.test(a));
      if (bad) { setError(`Not a valid address for this chain: ${bad}`); return; }
      // Lowercase + de-dupe client-side, mirroring the server (which folds case
      // then rejects dupes with a field error that has no detail/error_kind →
      // would surface as a confusing generic message). Catch it here clearly.
      // Solana addresses are case-SENSITIVE — never fold their case.
      const folded = chain === 'solana' ? addrs : addrs.map(a => a.toLowerCase());
      if (new Set(folded).size !== folded.length) {
        setError('That wallet address is listed more than once.');
        return;
      }
      body.chain = chain;
      body.wallet_addresses = folded;
    } else {
      body.venue_account_id = venueAccountId.trim();
    }

    setBusy(true);
    try {
      const acct = await api<{ id: string }>('/ops/accounts', { method: 'POST', body });
      await api(`/ops/funds/${fundId}/accounts`, {
        method: 'POST', body: { account_id: acct.id, funding },
      });
      reset();
      onAdded();
    } catch (err: any) {
      setError(dataSourceErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginBottom: 40 }}>
      <div className="label-s" style={{ color: 'var(--ink-3)', marginBottom: 16 }}>
        ADD A DATA SOURCE
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Field label="VENUE" required hint="The exchange account or on-chain wallet set this fund tracks.">
          <select className="field-input" value={venue}
                  onChange={e => { setVenue(e.target.value); setError(null); }}
                  style={{ borderColor: !venue ? 'var(--oxblood)' : undefined }}>
            <option value="" disabled>— Select venue —</option>
            {VENUE_OPTIONS.map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          {!venue && <div style={{ fontSize: 12, color: 'var(--oxblood)', marginTop: 4, fontFamily: 'var(--serif)', fontStyle: 'italic' }}>Select a venue to continue.</div>}
        </Field>

        {venue === 'onchain' ? (
          <>
            <Field label="CHAIN" hint="Leave on all-chains for Ethereum/BSC unless this wallet set is single-chain. Solana and Sui must be selected explicitly.">
              <select className="field-input" value={chain}
                      onChange={e => setChain(e.target.value)}>
                {CHAIN_OPTIONS.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="WALLET ADDRESSES" required hint={addressHintFor(chain)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {wallets.map((addr, i) => {
                  const touched = addr.length > 0;
                  const addrRe = addressRegexFor(chain);
                  const valid = !touched || addrRe.test(addr.trim());
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input className="field-input" value={addr} placeholder={chain === 'solana' ? 'Base58 address…' : '0x…'}
                               onChange={e => setWallets(ws => ws.map((w, j) => j === i ? e.target.value : w))}
                               style={{ fontFamily: 'var(--mono)', fontSize: 13,
                                        borderColor: !valid ? 'var(--oxblood)' : undefined }} />
                        {wallets.length > 1 && (
                          <button type="button" className="btn-link" style={{ color: 'var(--oxblood)' }}
                                  onClick={() => setWallets(ws => ws.filter((_, j) => j !== i))}>×</button>
                        )}
                      </div>
                      {!valid && (
                        <div style={{ fontSize: 12, color: 'var(--oxblood)', marginTop: 4, fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
                          {addressHintFor(chain)}
                        </div>
                      )}
                      {touched && valid && addr.trim().length > 0 && (
                        <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4, fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
                          Valid address.
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: 20 }}>
                  <button type="button" className="btn-link" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                          onClick={() => setWallets(ws => [...ws, ''])}>
                    + Add another address
                  </button>
                  <button type="button" className="btn-link" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                          onClick={() => setWallets(ws => {
                            // Validly-formatted per chain, no real funds behind any of them.
                            const placeholder = chain === 'solana'
                              ? '11111111111111111111111111111111'
                              : chain === 'sui'
                              ? '0x' + '0'.repeat(63) + '1'
                              : '0x0000000000000000000000000000000000000001';
                            const idx = ws.findIndex(w => !w.trim());
                            if (idx === -1) return [...ws, placeholder];
                            return ws.map((w, j) => j === idx ? placeholder : w);
                          })}>
                    Use a test address
                  </button>
                </div>
              </div>
            </Field>
            <Field label="NICKNAME" hint="Shown in the data-sources list.">
              <input className="field-input" value={nickname} placeholder="Treasury wallets"
                     onChange={e => setNickname(e.target.value)} />
            </Field>
          </>
        ) : venue ? (
          <>
            <Field label="NICKNAME" hint="Shown in the data-sources list.">
              <input className="field-input" value={nickname} placeholder="Fund · Coinbase Prime"
                     onChange={e => setNickname(e.target.value)} />
            </Field>
            <Field label="VENUE ACCOUNT ID (OPTIONAL)" hint="Portfolio / sub-account id for isolation. Leave blank for the key's default.">
              <input className="field-input" value={venueAccountId} placeholder="00000000-0000-0000-0000-000000000000"
                     onChange={e => setVenueAccountId(e.target.value)} />
            </Field>
          </>
        ) : null}

        {error && (
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic',
                        color: 'var(--oxblood)', fontSize: 14 }}>
            {error}
          </div>
        )}

        {venue && (
          <button type="submit" className="btn" disabled={busy} style={{ alignSelf: 'flex-start' }}>
            {busy ? 'Adding…' : <>Add data source <span className="btn-arrow">→</span></>}
          </button>
        )}
      </div>
    </form>
  );
}

// ─── wizard chrome ──────────────────────────────────────────────────────────

// Five-step path: Basics → Data sources → Investors → Strike NAV → Go live. The
// stepper shows the full journey; the current step is in ink, completed steps
// accent, upcoming steps muted. (Steps 4/5 land in sub-slices 5d/5e.)
function Stepper({ current }: { current: number }) {
  const steps = [
    { n: 1, label: 'Basics' },
    { n: 2, label: 'Data sources' },
    { n: 3, label: 'Investors' },
    { n: 4, label: 'Strike NAV' },
    { n: 5, label: 'Go live' },
  ];
  return (
    <div style={{ display: 'flex', gap: 32, alignItems: 'center', marginBottom: 40,
                  borderBottom: 'var(--hairline)', paddingBottom: 16 }}>
      {steps.map(s => {
        const isCurrent = s.n === current;
        const isComplete = s.n < current;
        return (
          <div key={s.n} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: isCurrent ? 'var(--ink)' : 'var(--ink-3)',
          }}>
            <span className="num" style={{ color: isComplete ? 'var(--accent)' : undefined }}>
              {`0${s.n}`}
            </span>
            <span>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Summary({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="label-s" style={{ color: 'var(--ink-3)', marginBottom: 6 }}>{label}</div>
      <div className={mono ? 'num' : undefined} style={{ fontSize: 18 }}>{value}</div>
    </div>
  );
}

// ─── form layout helpers (mirror ClientOnboard.tsx) ─────────────────────────

function Section({ number, label, children }: {
  number: string; label: string; children: React.ReactNode;
}) {
  return (
    <section>
      <div style={{
        fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 16,
      }}>
        {number} · {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, hint, required, children }: {
  label: string; hint?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--oxblood)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {hint && (
        <div style={{
          fontSize: 12, color: 'var(--ink-3)', marginTop: 6,
          fontFamily: 'var(--serif)', fontStyle: 'italic',
        }}>
          {hint}
        </div>
      )}
    </div>
  );
}
